import React, { useState, useRef, useEffect } from 'react';
import {
  Mic,
  Square,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  X,
  Volume2,
  FileAudio,
  Sparkles,
  Upload,
  AlertTriangle,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { Account } from '../../types';

/**
 * Agents send in call recordings straight off the handset, and those files are
 * routinely tens of megabytes. The cap keeps a stray hour-long recording from
 * being pushed at Cloud Storage over a field connection.
 */
const MAX_AUDIO_BYTES = 5 * 1024 * 1024;

/**
 * A RECORDER SET UP FOR SPEECH, NOT FOR MUSIC.
 *
 * WHAT IT WAS DOING. `new MediaRecorder(stream)` with no settings takes the
 * browser's default bitrate, which is tuned for general audio -- around
 * 128 kbps. That is about a megabyte for every minute recorded. A two minute
 * note from an agent standing at a borrower's door was roughly 2 MB, and
 * nothing ever deletes it.
 *
 * WHY 32 kbps IS THE RIGHT NUMBER. These recordings are one person talking.
 * Opus at 32 kbps mono carries speech cleanly -- it is the range used for
 * voice calls and podcasts, and an agent's note is neither harder nor more
 * valuable than either. The saving is about four to one: a minute goes from
 * roughly 960 KB to 240 KB, with nothing lost that anybody listening to the
 * note would notice.
 *
 * WHY IT TRIES A LIST. Browsers disagree about formats: Chrome and Android
 * record webm/opus, Safari records mp4/aac, and asking for a format the
 * browser does not support makes the constructor THROW -- which would have
 * meant no recording at all rather than a large one. Each option is checked
 * before it is used, and the last resort is the plain recorder that was
 * there before, so the worst case is exactly today's behaviour.
 */
function buildSpeechRecorder(stream: MediaStream): MediaRecorder {
  const SPEECH_BITRATE = 32000;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];

  for (const mimeType of candidates) {
    try {
      const supported =
        typeof MediaRecorder.isTypeSupported !== 'function' ||
        MediaRecorder.isTypeSupported(mimeType);
      if (!supported) continue;
      return new MediaRecorder(stream, { mimeType, audioBitsPerSecond: SPEECH_BITRATE });
    } catch {
      /* try the next one */
    }
  }

  // Nothing matched. Ask for the bitrate alone -- a browser that ignores it
  // simply behaves as it did before.
  try {
    return new MediaRecorder(stream, { audioBitsPerSecond: SPEECH_BITRATE });
  } catch {
    return new MediaRecorder(stream);
  }
}

/** Sizes as a person would say them, so the refusal message reads plainly. */
const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
};

interface VoiceNoteRecorderModalProps {
  account: Account;
  visitId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (voiceRecord: any) => void;
}

export const VoiceNoteRecorderModal: React.FC<VoiceNoteRecorderModalProps> = ({
  account,
  visitId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser, recordVoiceNote } = useSRMS();

  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingSeconds, setRecordingSeconds] = useState<number>(0);
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [noteTitle, setNoteTitle] = useState<string>(
    `Field Visit Statement - ${account.customerName}`
  );
  const [transcription, setTranscription] = useState<string>(
    'Customer explained business revenue delay. Promised ₹15,000 payment by end of week.'
  );
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [savedRecord, setSavedRecord] = useState<any | null>(null);

  // An audio file picked off the phone instead of recorded here. Name and size
  // are kept so the agent can see what is about to be sent.
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [uploadedBytes, setUploadedBytes] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<any>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      handleReset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioChunksRef.current = [];
        const recorder = buildSpeechRecorder(stream);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        recorder.onstop = () => {
          // The recorder's own type, not a guess. Safari records mp4, not
          // webm, and labelling an mp4 as webm makes it unplayable.
          const audioBlob = new Blob(audioChunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });
          const url = URL.createObjectURL(audioBlob);
          setAudioBlobUrl(url);
          stream.getTracks().forEach((track) => track.stop());
        };

        recorder.start();
        setIsRecording(true);
        setRecordingSeconds(0);
      } else {
        // Fallback simulate timer
        setIsRecording(true);
        setRecordingSeconds(0);
      }
    } catch {
      // Permission might be denied in some iframe sandboxes, enable simulation timer
      setIsRecording(true);
      setRecordingSeconds(0);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    } else if (!audioBlobUrl) {
      // Create empty mock audio blob
      setAudioBlobUrl('mock_audio_url');
    }
    setIsRecording(false);
  };

  /**
   * Take an existing recording instead of making one here.
   *
   * The file becomes an object URL, which is exactly what the recorder
   * produces, so it goes on to Cloud Storage down the same save path with
   * nothing special about it.
   */
  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    // The box is cleared either way, so picking the same file again after a
    // refusal still fires a change.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (file.size > MAX_AUDIO_BYTES) {
      setUploadError(
        `"${file.name}" is ${formatFileSize(file.size)}. The limit is 5 MB, so it has not been attached. Please send a shorter recording or a smaller file.`
      );
      return;
    }

    if (isRecording) stopRecording();
    setUploadError(null);
    setUploadedName(file.name);
    setUploadedBytes(file.size);
    setRecordingSeconds(0);
    setIsPlaying(false);
    setAudioBlobUrl(URL.createObjectURL(file));
  };

  /**
   * An uploaded file carries its own length, so the audit record shows the real
   * duration rather than the fallback. Some containers report no usable length,
   * hence the check.
   */
  const handleLoadedMetadata = () => {
    if (!uploadedName) return;
    const secs = audioPlayerRef.current?.duration;
    if (typeof secs === 'number' && isFinite(secs) && secs > 0) {
      setRecordingSeconds(Math.round(secs));
    }
  };

  const handleReset = () => {
    if (audioPlayerRef.current) {
      try {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
      } catch {}
    }
    if (isRecording) stopRecording();
    setIsRecording(false);
    setRecordingSeconds(0);
    setAudioBlobUrl(null);
    setIsPlaying(false);
    setIsSaved(false);
    setSavedRecord(null);
    setUploadedName(null);
    setUploadedBytes(0);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const togglePlayback = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        try {
          audioPlayerRef.current.pause();
        } catch {}
        setIsPlaying(false);
      } else {
        const playPromise = audioPlayerRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((err) => {
              setIsPlaying(false);
              if (err?.name !== 'AbortError') {
                console.debug('Audio play request interrupted or prevented:', err);
              }
            });
        }
      }
    }
  };

  const handleSaveToDrive = () => {
    const duration = recordingSeconds > 0 ? recordingSeconds : 28;
    const record = recordVoiceNote({
      accountId: account.accountId,
      durationSeconds: duration,
      title: noteTitle.trim() || `Audio Note - ${account.accountId}`,
      transcription: transcription.trim(),
      audioBlobUrl: audioBlobUrl || undefined,
      visitId,
    });

    setSavedRecord(record);
    setIsSaved(true);
    if (onSuccess) onSuccess(record);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto font-sans">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl overflow-y-auto max-h-[92vh] text-slate-800 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-pink-600 rounded-xl text-white shadow-sm">
              <Mic className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span>Record Audio Voice Note</span>
                <span className="text-xs bg-pink-50 text-pink-700 font-mono font-bold px-2 py-0.5 rounded border border-pink-200">
                  {account.accountId}
                </span>
              </h2>
              <p className="text-xs text-slate-500">
                Direct audio storage in Cloud Storage &amp; Account Timeline link
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-xl transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 text-xs">
          {isSaved && savedRecord ? (
            /* Saved Success Screen */
            <div className="text-center space-y-4 py-3">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-200">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Voice Note Saved to Cloud Storage</h3>
                <p className="text-slate-500 text-xs mt-1">
                  Saved with Ref ID <span className="font-mono text-blue-700 font-semibold">{savedRecord.driveFileId}</span> and linked to Account Timeline.
                </p>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-left space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Title:</span>
                  <span className="font-semibold text-slate-900">{savedRecord.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration:</span>
                  <span className="font-mono text-emerald-700 font-bold">{savedRecord.durationSeconds}s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Agent:</span>
                  <span className="text-slate-900 font-medium">{currentUser.name} ({currentUser.agentId || currentUser.username || currentUser.id})</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl border border-slate-200 cursor-pointer transition"
                >
                  Record Another
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl shadow-sm cursor-pointer transition"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Recording Interface */
            <div className="space-y-4">
              {/* Visual Recording Pod */}
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 flex flex-col items-center justify-center space-y-3">
                {/* Pulsing Mic Circle */}
                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                    isRecording
                      ? 'bg-red-100 border-2 border-red-500 animate-pulse text-red-600 shadow-lg shadow-red-500/10'
                      : audioBlobUrl
                      ? 'bg-pink-100 border-2 border-pink-500 text-pink-700'
                      : 'bg-white border border-slate-200 text-slate-400 shadow-2xs'
                  }`}
                >
                  <Mic className={`w-8 h-8 ${isRecording ? 'animate-bounce' : ''}`} />
                </div>

                {/* Timer Display */}
                <div className="text-center">
                  <div className="text-2xl font-mono font-black tracking-wider text-slate-900">
                    {Math.floor(recordingSeconds / 60)
                      .toString()
                      .padStart(2, '0')}
                    :
                    {(recordingSeconds % 60).toString().padStart(2, '0')}
                  </div>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {isRecording
                      ? '🔴 Recording audio statement...'
                      : uploadedName
                      ? '✅ Audio file attached. Ready to upload.'
                      : audioBlobUrl
                      ? '✅ Recording complete. Ready to upload.'
                      : 'Tap record button to start speaking, or attach a file below'}
                  </span>
                </div>

                {/* Simulated Audio Waveform visualizer */}
                {isRecording && (
                  <div className="flex items-center gap-1 h-6">
                    {[16, 24, 12, 28, 20, 14, 26, 18, 10, 22, 16, 24].map((h, i) => (
                      <div
                        key={i}
                        className="w-1 bg-red-500 rounded-full animate-pulse"
                        style={{
                          height: `${Math.max(6, (h * (recordingSeconds % 4 + 1)) / 3)}px`,
                          animationDelay: `${i * 80}ms`,
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Action Controls */}
                <div className="flex items-center gap-3 pt-1">
                  {!isRecording && !audioBlobUrl && (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-sm text-sm transition cursor-pointer"
                    >
                      <Mic className="w-4 h-4" />
                      <span>Start Recording</span>
                    </button>
                  )}

                  {isRecording && (
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="px-6 py-2.5 bg-slate-900 hover:bg-black text-white font-bold rounded-xl flex items-center gap-2 shadow-sm text-sm transition cursor-pointer"
                    >
                      <Square className="w-4 h-4 fill-current" />
                      <span>Stop Recording</span>
                    </button>
                  )}

                  {audioBlobUrl && !isRecording && (
                    <>
                      <button
                        type="button"
                        onClick={togglePlayback}
                        className="px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-700 font-semibold rounded-xl flex items-center gap-2 border border-pink-200 cursor-pointer transition"
                      >
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        <span>{isPlaying ? 'Pause' : 'Play Audio'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleReset}
                        className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 rounded-xl border border-slate-200 cursor-pointer transition"
                        title={uploadedName ? 'Discard this file' : 'Discard & Re-record'}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>

                {audioBlobUrl && audioBlobUrl !== 'mock_audio_url' && (
                  <audio
                    ref={audioPlayerRef}
                    src={audioBlobUrl}
                    onEnded={() => setIsPlaying(false)}
                    onLoadedMetadata={handleLoadedMetadata}
                    className="hidden"
                  />
                )}
              </div>

              {/* Attach a recording made elsewhere, typically a call recording
                  off the agent's own handset. */}
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-4 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-slate-700 font-semibold">
                    <FileAudio className="w-4 h-4 text-pink-600" />
                    <span>Or attach an existing audio file</span>
                  </div>
                  <label className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl border border-slate-200 flex items-center gap-1.5 cursor-pointer transition">
                    <Upload className="w-3.5 h-3.5" />
                    <span>Choose Audio File</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleFileChosen}
                      className="hidden"
                    />
                  </label>
                </div>

                <p className="text-[11px] text-slate-500">
                  Customer call recordings up to 5 MB. Larger files are refused.
                </p>

                {uploadedName && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-semibold truncate">{uploadedName}</span>
                    <span className="font-mono text-[11px] text-emerald-700 shrink-0">
                      {formatFileSize(uploadedBytes)}
                    </span>
                  </div>
                )}

                {uploadError && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-rose-800">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span className="font-medium">{uploadError}</span>
                  </div>
                )}
              </div>

              {/* Title & Notes */}
              <div className="space-y-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Voice Note Title:
                  </label>
                  <input
                    type="text"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="e.g. Customer Commitment & Shop Status"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-pink-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Summary Notes / Key Talking Points:
                  </label>
                  <textarea
                    rows={2}
                    value={transcription}
                    onChange={(e) => setTranscription(e.target.value)}
                    placeholder="Key highlights of discussion for supervisor and audit record..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-pink-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Upload to Drive CTA */}
              <div className="pt-2">
                <button
                  type="button"
                  disabled={isRecording || (!audioBlobUrl && recordingSeconds === 0)}
                  onClick={handleSaveToDrive}
                  className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition ${
                    audioBlobUrl || recordingSeconds > 0
                      ? 'bg-pink-600 hover:bg-pink-700 text-white cursor-pointer'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Save Voice Note to Cloud Storage &amp; Timeline</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
